import type { Color } from "@/lib/product-utils";

const HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const COLOR_SELECTION_KEY_SEPARATOR = "\u0000";

function getTrimmedString(value: string | null | undefined): string | undefined {
  if (typeof value !== "string") return undefined;

  const trimmedValue = value.trim();
  return trimmedValue || undefined;
}

function getNormalizedColorLabel(color: Color | null | undefined): string | undefined {
  return getTrimmedString(color?.label);
}

export function getSafeHexColor(hexCode: string | null | undefined): string | undefined {
  const trimmedHexCode = getTrimmedString(hexCode);
  if (!trimmedHexCode) return undefined;

  return HEX_COLOR_PATTERN.test(trimmedHexCode) ? trimmedHexCode : undefined;
}

export function getSafeSwatchUrl(swatchUrl: string | null | undefined): string | undefined {
  const trimmedSwatchUrl = getTrimmedString(swatchUrl);
  if (!trimmedSwatchUrl) return undefined;
  if (trimmedSwatchUrl.startsWith("//")) return undefined;
  if (trimmedSwatchUrl.startsWith("/")) return encodeURI(trimmedSwatchUrl).replace(/#/g, "%23");

  try {
    const parsedUrl = new URL(trimmedSwatchUrl);
    if (parsedUrl.protocol !== "https:") return undefined;
    return parsedUrl.toString();
  } catch {
    return undefined;
  }
}

export function getColorSelectionKey(color: Color | null | undefined): string | undefined {
  if (!color) return undefined;

  return [
    getNormalizedColorLabel(color) ?? "",
    getSafeHexColor(color.hex_code) ?? "",
    getSafeSwatchUrl(color.swatch_url) ?? "",
  ].join(COLOR_SELECTION_KEY_SEPARATOR);
}

export function isSelectedColorMatch(
  selectedColor: Color | null | undefined,
  color: Color | null | undefined
): boolean {
  if (!selectedColor || !color) return false;

  if (getColorSelectionKey(selectedColor) === getColorSelectionKey(color)) {
    return true;
  }

  const selectedLabel = getNormalizedColorLabel(selectedColor);
  const colorLabel = getNormalizedColorLabel(color);
  if (!selectedLabel || selectedLabel !== colorLabel) return false;

  const selectedHasStructuredColorData =
    typeof getSafeHexColor(selectedColor.hex_code) === "string" ||
    typeof getSafeSwatchUrl(selectedColor.swatch_url) === "string";

  // Keep a label-only fallback for older persisted selection state that predates
  // structured swatch metadata; duplicate labels may still collide in this mode.
  return !selectedHasStructuredColorData;
}

export function hasCompleteColorSwatchData(colors: Color[]): boolean {
  if (colors.length === 0) return false;

  return colors.every((color) => {
    const hasHex = typeof getSafeHexColor(color.hex_code) === "string";
    const hasSwatchUrl = typeof getSafeSwatchUrl(color.swatch_url) === "string";
    return hasHex || hasSwatchUrl;
  });
}
